import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  IStationeryPurchaseOrderDetailedItem,
  IStationeryPurchaseOrderListItem,
} from 'src/types/stationery-purchase-order';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  orders: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IStationeryPurchaseOrderListItem[];
  };
  order: IStationeryPurchaseOrderDetailedItem | null;
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  orders: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  order: null,
};

const stationeryPurchaseOrderSlice = createSlice({
  name: 'stationeryPurchaseOrder',
  initialState,
  reducers: {
    removeStationeryPurchaseOrder: (state, action) => {
      const { orderId } = action.payload;

      const index = state.orders.items.findIndex((order) => order._id === orderId);

      if (index > -1) {
        action.payload.index = index;
        state.orders.items = state.orders.items.filter((order) => order._id !== orderId);
      }
    },
    rollbackStationeryPurchaseOrderDeletion: (state, action) => {
      const { order, index } = action.payload;

      state.orders.items.splice(index, 0, order);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStationeryPurchaseOrders.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getStationeryPurchaseOrders.fulfilled, (state, action) => {
        state.status = 'success';
        state.orders = action.payload;
      })
      .addCase(getStationeryPurchaseOrders.rejected, (state, action) => {
        state.orders = initialState.orders;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getStationeryPurchaseOrderById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getStationeryPurchaseOrderById.fulfilled, (state, action) => {
        state.status = 'success';
        state.order = action.payload;
        state.error = initialState.error;
      })
      .addCase(getStationeryPurchaseOrderById.rejected, (state, action) => {
        state.order = initialState.order;
        state.status = 'failure';
        state.error = action.error.message;
      });
  },
});

export default stationeryPurchaseOrderSlice.reducer;

export const { removeStationeryPurchaseOrder, rollbackStationeryPurchaseOrderDeletion } =
  stationeryPurchaseOrderSlice.actions;

export const getStationeryPurchaseOrders = createAsyncThunk(
  'stationeryPurchaseOrder/paginated',
  async (params: {
    page: number;
    limit: number;
    generalSearch?: string | null;
    supplier?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
    sortBy?: string | null;
    sort?: 'asc' | 'desc' | null;
  }) => {
    const { page, limit, generalSearch, supplier, startDate, endDate, sortBy, sort } = params;

    try {
      const response = await axios.get(
        `stationery/purchase-order?page=${page + 1}&limit=${limit}&generalSearch=${
          generalSearch ?? ''
        }&supplier=${supplier ?? ''}&startDate=${startDate ? startDate.toISOString() : ''}&endDate=${
          endDate ? endDate.toISOString() : ''
        }&sortBy=${sortBy}&sort=${sort ?? ''}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getStationeryPurchaseOrderById = createAsyncThunk(
  'stationeryPurchaseOrder/getById',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`stationery/purchase-order/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deleteStationeryPurchaseOrder = createAsyncThunk(
  'stationeryPurchaseOrder/delete',
  async (params: { order: IStationeryPurchaseOrderListItem; index: number }, { dispatch }) => {
    const { order, index } = params;

    try {
      const response = await axios.delete(`stationery/purchase-order/${order._id}`);
      return response.data.data;
    } catch (err) {
      dispatch(rollbackStationeryPurchaseOrderDeletion({ order, index }));
      throw err;
    }
  }
);

export interface TCreateStationeryPurchaseOrderItem {
  stationeryItem: string;
  quantity: number;
  unitPrice?: number | null;
  discount: number;
  totalAfterDiscount: number;
}

export interface TCreateStationeryPurchaseOrder {
  supplier: string;
  purchaseDate: Date;
  discount: number;
  isPercentageDiscount: boolean;
  totalAfterDiscount: number;
  remarks: string | null;
  items: TCreateStationeryPurchaseOrderItem[];
}

export const createStationeryPurchaseOrder = createAsyncThunk(
  'stationeryPurchaseOrder/create',
  async (params: { poTransaction: TCreateStationeryPurchaseOrder }) => {
    const { poTransaction } = params;
    const response = await axios.post('stationery/purchase-order', poTransaction);
    return response.data.data;
  }
);

export const updateStationeryPurchaseOrder = createAsyncThunk(
  'stationeryPurchaseOrder/update',
  async (params: {
    purchaseOrderId: string;
    poTransaction: TCreateStationeryPurchaseOrder;
  }) => {
    const { poTransaction, purchaseOrderId } = params;
    const response = await axios.put(
      `stationery/purchase-order/${purchaseOrderId}`,
      poTransaction
    );
    return response.data.data;
  }
);
